/**
 * Batched initial load for the external PA orders worklist: runs multiple Postgres searches and merges hits.
 */
import { getBearerToken } from "../../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../../data-model/postgresResponseForMedicalPaOrders";
import { ExternalTableState } from "../hooks/useExternalTableData";
import { generateAllPaOrdersForExternalByAlgoliaTableData } from "../utils/allPaOrdersForExternalByAlgoliaHelper";

/**
 * Fetches initial external PA orders data from Postgres by executing multiple batch payloads
 * sequentially, combining the results, and transforming them into table-ready data.
 *
 * @param payloads - Array of search payloads, one per batch page to fetch
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setExternalTableState - State setter for loading/error/data lifecycle
 * @param setTableData - State setter for the transformed table rows
 * @param setInitialDataFetchingExecuted - Flag setter indicating initial fetch is complete
 * @returns Promise that settles after all batches finish; failures are stored on `setExternalTableState` instead of throwing.
 */
export const makeAlgoliaCallForInitialDataFetch = async (
  payloads: SearchForMedicalPaOrdersFromPostgresParams[],
  authStatusOptions: AuthStatusOptionModel[],
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setExternalTableState((prev) => ({ ...prev, loading: true }));

  try {
    await getBearerToken();

    const data: PostgresResponseForMedicalPaOrders[] = [];

    for (const payload of payloads) {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      const parsedResponse = JSON.parse(
        response,
      ) as PostgresResponseForMedicalPaOrders;
      data.push(parsedResponse);
    }

    const combinedData = appendAllResponses(data);
    setExternalTableState((prev) => ({ ...prev, data: combinedData }));

    const tableData = generateAllPaOrdersForExternalByAlgoliaTableData(
      combinedData.hits ?? [],
      authStatusOptions,
    );
    setTableData(tableData);
  } catch (error) {
    setExternalTableState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setExternalTableState((prev) => ({ ...prev, loading: false }));
    setInitialDataFetchingExecuted(true);
  }
};

/**
 * Merges multiple Postgres response objects into a single response,
 * concatenating all hits arrays while preserving the last response's metadata.
 *
 * @param responses - Array of Postgres response objects to merge
 * @returns A single combined response with all hits concatenated
 */
const appendAllResponses = (
  responses: PostgresResponseForMedicalPaOrders[],
) => {
  return responses.reduce(
    (acc, response) => ({
      ...response,
      hits: [...(acc.hits ?? []), ...(response.hits ?? [])],
    }),
    {} as PostgresResponseForMedicalPaOrders,
  );
};
